<template>
  <div class="fw-page">
    <!-- Header -->
    <div class="fw-header">
      <h1 class="fw-title">Find Words</h1>
      <p class="fw-subtitle">Trace each word by following the connected letters</p>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="fw-loading">
      <div class="fw-spinner"></div>
      <p>Building puzzle…</p>
    </div>

    <!-- Game area -->
    <div v-else-if="!gameOver" class="fw-game">

      <!-- Top bar: hint button sits above the grid -->
      <div class="fw-top-bar">
        <div class="fw-hint-wrap">
          <button class="fw-btn fw-btn--hint" @click="giveHint" :disabled="hintRunning">
            💡 Hint
          </button>
          <transition name="fw-fade">
            <div class="fw-hint-bar-track" v-if="hintRunning">
              <div class="fw-hint-bar-fill" :style="{ width: hintProgress + '%' }"></div>
            </div>
          </transition>
        </div>
      </div>

      <!-- Grid -->
      <div class="fw-grid-wrap">
        <div
          class="fw-grid"
          :style="{ gridTemplateColumns: `repeat(${gridSize}, 1fr)` }"
        >
          <div
            v-for="(cell, idx) in grid"
            :key="idx"
            class="fw-cell"
            :class="[
              cell.wordIndex !== null && words[cell.wordIndex].found ? `fw-word-${cell.wordIndex}` : '',
              cell.wordIndex !== null && words[cell.wordIndex].found ? 'fw-cell--found' : '',
              cell.wordIndex !== null && !words[cell.wordIndex].found ? 'fw-cell--unplayed' : '',
              cell.wordIndex === null ? 'fw-cell--empty' : '',
              isSelected(idx) ? `fw-cell--selected fw-sel-${activeWordIndex}` : '',
              cell.isStart && words[cell.wordIndex] && words[cell.wordIndex].found ? 'fw-cell--start' : '',
            ]"
            @mousedown="onCellMouseDown(idx)"
            @mouseenter="onCellMouseEnter(idx)"
            @mouseup="onCellMouseUp"
            @touchstart.prevent="onCellMouseDown(idx)"
            @touchmove.prevent="onTouchMove($event)"
            @touchend.prevent="onCellMouseUp"
          >
            <span v-if="cell.letter" class="fw-cell__letter">{{ cell.letter.toUpperCase() }}</span>
            <span v-if="cell.isStart && words[cell.wordIndex] && words[cell.wordIndex].found" class="fw-cell__check">✓</span>
          </div>
        </div>
      </div>

      <!-- Word list: fixed rows, one per word, order never changes -->
      <div class="fw-word-list">
        <div
          v-for="(w, i) in words"
          :key="i"
          class="fw-word-tag"
          :class="[w.found ? `fw-word-tag--done fw-word-tag--col-${i}` : '']"
        >
          <!-- Found: coloured letters + tick -->
          <template v-if="w.found">
            <span
              v-for="(letter, li) in w.text.split('')"
              :key="li"
              class="fw-word-tag__letter"
            >{{ letter.toUpperCase() }}</span>
            <span class="fw-word-tag__check">✓</span>
          </template>

          <!-- Active (being traced right now): fill slots live -->
          <template v-else-if="activeWordIndex === i">
            <span
              v-for="(slot, si) in activeRowSlots"
              :key="'a-' + si"
              class="fw-word-tag__letter"
              :class="slot.filled
                ? `fw-word-tag__letter--traced fw-word-tag__letter--traced-${i}`
                : 'fw-word-tag__letter--hidden'"
            >{{ slot.filled ? slot.letter : '' }}</span>
          </template>

          <!-- Idle unfound: blank boxes matching this word's length -->
          <template v-else>
            <span
              v-for="s in w.text.length"
              :key="s"
              class="fw-word-tag__letter fw-word-tag__letter--hidden"
            ></span>
          </template>
        </div>
      </div>

      <!-- Controls -->
      <div class="fw-controls">
        <button class="fw-btn fw-btn--secondary" @click="newGame">New Game</button>
      </div>

      <!-- Wrong flash message -->
      <transition name="fw-fade">
        <div v-if="showWrong" class="fw-wrong-msg">Not a word - try again!</div>
      </transition>
    </div>

    <!-- Victory screen -->
    <div v-else class="fw-victory">
      <div class="fw-victory__icon">🎉</div>
      <h2 class="fw-victory__title">Puzzle Solved!</h2>
      <p class="fw-victory__sub">You found all {{ words.length }} words!</p>
      <button class="fw-btn fw-btn--primary" @click="newGame">Play Again</button>
    </div>
  </div>
</template>

<script>
var randomWords = require("random-words");

const WORD_COLOURS = [
  { bg: "#f97316", text: "#fff" },   // orange
  { bg: "#22c55e", text: "#fff" },   // green
  { bg: "#06b6d4", text: "#fff" },   // cyan
  { bg: "#8b5cf6", text: "#fff" },   // purple
  { bg: "#ec4899", text: "#fff" },   // pink
  { bg: "#eab308", text: "#1e293b" } // yellow
];

export default {
  name: "FindWords",

  data() {
    return {
      loading: true,
      gridSize: 7,
      grid: [],
      words: [],
      selecting: false,
      selectionPath: [],
      activeWordIndex: -1,
      foundWords: [],      // [{ text, colorIndex }] - in order of discovery
      showWrong: false,
      wrongTimer: null,
      gameOver: false,
      hintRunning: false,
      hintProgress: 100,
      hintInterval: null,
    };
  },

  computed: {
    // Slots for the active row while the user is dragging
    activeRowSlots() {
      if (this.activeWordIndex < 0 || this.selectionPath.length === 0) return [];
      const word = this.words[this.activeWordIndex];
      const cellIndices = word.cellIndices;

      const firstSelected = this.selectionPath[0];
      const isReverse = firstSelected === cellIndices[cellIndices.length - 1];

      return cellIndices.map((_, i) => {
        const cellIdx = isReverse
          ? cellIndices[cellIndices.length - 1 - i]
          : cellIndices[i];
        const isFilled = this.selectionPath.includes(cellIdx);
        const letter = isReverse
          ? word.text[word.text.length - 1 - i]
          : word.text[i];
        return { filled: isFilled, letter: letter.toUpperCase() };
      });
    },
  },

  mounted() {
    this.newGame();
    window.addEventListener("mouseup", this.onCellMouseUp);
  },

  beforeDestroy() {
    window.removeEventListener("mouseup", this.onCellMouseUp);
    if (this.wrongTimer) clearTimeout(this.wrongTimer);
    this.stopHint();
  },

  methods: {
    // ─── Game setup ─────────────────────────────────────────────────────────

    newGame() {
      this.loading = true;
      this.stopHint();
      this.gameOver = false;
      this.selecting = false;
      this.selectionPath = [];
      this.activeWordIndex = -1;
      this.foundWords = [];
      this.showWrong = false;

      setTimeout(() => {
        this.buildPuzzle();
        this.loading = false;
      }, 80);
    },

    buildPuzzle() {
      const size = this.gridSize;
      const totalCells = size * size;

      // 1. Generate words and keep only those that can realistically fit
      let candidates = randomWords({ min: 6, max: 10, minLength: 6, maxLength: 12 });
      // Deduplicate
      candidates = [...new Set(candidates)];

      // 2. Try to place as many words as possible (4–6)
      const grid = Array(totalCells).fill(null).map(() => ({
        letter: "",
        wordIndex: null,
        posInWord: null,
        isStart: false,
      }));

      const placed = [];
      let wordIdx = 0;

      for (const word of candidates) {
        if (placed.length >= 6) break;
        const cellPath = this.placeWord(word, grid, size);
        if (cellPath) {
          cellPath.forEach((cellIndex, pos) => {
            grid[cellIndex].letter = word[pos];
            grid[cellIndex].wordIndex = wordIdx;
            grid[cellIndex].posInWord = pos;
            grid[cellIndex].isStart = pos === 0;
          });
          placed.push({
            text: word,
            found: false,
            cellIndices: cellPath,
          });
          wordIdx++;
        }
        if (placed.length >= 4 && placed.length < 6 && candidates.indexOf(word) > candidates.length - 3) break;
      }

      // Need at least 3 words; if we couldn't place enough, retry with smaller words
      if (placed.length < 3) {
        this.buildPuzzle();
        return;
      }

      this.grid = grid;
      this.words = placed;
    },

    /**
     * Try to place a word on the grid using a random snake path.
     * Returns an array of cell indices, or null if placement failed.
     */
    placeWord(word, grid, size) {
      const maxAttempts = 80;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const path = this.tryPath(word, grid, size);
        if (path) return path;
      }
      return null;
    },

    tryPath(word, grid, size) {
      const totalCells = size * size;
      // Pick a random free starting cell
      const freeCells = [];
      for (let i = 0; i < totalCells; i++) {
        if (grid[i].wordIndex === null) freeCells.push(i);
      }
      if (freeCells.length < word.length) return null;

      const startIdx = freeCells[Math.floor(Math.random() * freeCells.length)];
      const path = [startIdx];
      const visited = new Set([startIdx]);

      for (let pos = 1; pos < word.length; pos++) {
        const current = path[path.length - 1];
        const neighbors = this.getNeighbors(current, size).filter(
          (n) => !visited.has(n) && grid[n].wordIndex === null
        );
        if (neighbors.length === 0) return null;
        const next = neighbors[Math.floor(Math.random() * neighbors.length)];
        path.push(next);
        visited.add(next);
      }

      return path;
    },

    getNeighbors(idx, size) {
      const row = Math.floor(idx / size);
      const col = idx % size;
      const neighbors = [];
      if (row > 0) neighbors.push(idx - size);
      if (row < size - 1) neighbors.push(idx + size);
      if (col > 0) neighbors.push(idx - 1);
      if (col < size - 1) neighbors.push(idx + 1);
      return neighbors;
    },

    // ─── Interaction ─────────────────────────────────────────────────────────

    onCellMouseDown(idx) {
      if (this.grid[idx].wordIndex === null) return;
      if (this.words[this.grid[idx].wordIndex].found) return;
      this.selecting = true;
      this.activeWordIndex = this.grid[idx].wordIndex;
      this.selectionPath = [idx];
    },

    onCellMouseEnter(idx) {
      if (!this.selecting) return;
      if (this.grid[idx].wordIndex === null) return;

      const last = this.selectionPath[this.selectionPath.length - 1];

      // Allow backtracking: if user swipes back to previous cell, pop
      if (this.selectionPath.length > 1 && this.selectionPath[this.selectionPath.length - 2] === idx) {
        this.selectionPath.pop();
        return;
      }

      // Must be adjacent, not already in path, and same word
      if (this.selectionPath.includes(idx)) return;
      if (!this.getNeighbors(last, this.gridSize).includes(idx)) return;
      if (this.grid[idx].wordIndex !== this.activeWordIndex) return;

      this.selectionPath.push(idx);

      // Auto-complete: check if the full word is now traced
      this.checkSelectionLive();
    },

    onCellMouseUp() {
      if (!this.selecting) return;
      this.selecting = false;
      // Only show wrong flash if the user actually dragged (more than 1 cell)
      // and didn't complete the word - a single click just resets silently
      if (this.selectionPath.length > 1) {
        this.triggerWrong();
      }
      this.selectionPath = [];
      this.activeWordIndex = -1;
    },

    onTouchMove(event) {
      const touch = event.touches[0];
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      if (!el) return;
      const cellEl = el.closest(".fw-cell");
      if (!cellEl) return;
      const allCells = Array.from(document.querySelectorAll(".fw-cell"));
      const idx = allCells.indexOf(cellEl);
      if (idx !== -1) this.onCellMouseEnter(idx);
    },

    // Check on every new cell added - complete the word as soon as the full path is traced
    checkSelectionLive() {
      if (this.selectionPath.length === 0) return;

      const wordIdx = this.activeWordIndex;
      const word = this.words[wordIdx];

      const matchesWord =
        this.selectionPath.length === word.cellIndices.length &&
        this.selectionPath.every((ci, i) => ci === word.cellIndices[i]);

      const reversed = [...word.cellIndices].reverse();
      const matchesReversed =
        this.selectionPath.length === reversed.length &&
        this.selectionPath.every((ci, i) => ci === reversed[i]);

      if (matchesWord || matchesReversed) {
        this.selecting = false;
        this.words[wordIdx].found = true;
        // Push to found list with the word's colour index
        this.foundWords.push({ text: word.text, colorIndex: wordIdx });
        this.selectionPath = [];
        this.activeWordIndex = -1;
        this.stopHint();
        if (this.words.every((w) => w.found)) {
          setTimeout(() => { this.gameOver = true; }, 600);
        }
      }
    },

    triggerWrong() {
      this.showWrong = true;
      if (this.wrongTimer) clearTimeout(this.wrongTimer);
      this.wrongTimer = setTimeout(() => { this.showWrong = false; }, 1800);
    },

    isSelected(idx) {
      return this.selectionPath.includes(idx);
    },

    giveHint() {
      if (this.hintRunning) return;
      // Find the first unfound word
      const unfound = this.words.find((w) => !w.found);
      if (!unfound) return;

      const startCell = unfound.cellIndices[0];
      this.hintRunning = true;
      this.hintProgress = 100;
      // Highlight the first cell immediately
      this.selectionPath = [startCell];

      const totalMs = 3000;
      const tickMs  = 60;
      const steps   = totalMs / tickMs;
      let tick = 0;

      this.hintInterval = setInterval(() => {
        tick++;
        this.hintProgress = Math.max(0, 100 - (tick / steps) * 100);

        if (tick >= steps) {
          // Bar done - clear highlight, let user try
          this.stopHint();
          this.selectionPath = [];
        }
      }, tickMs);
    },

    stopHint() {
      if (this.hintInterval) {
        clearInterval(this.hintInterval);
        this.hintInterval = null;
      }
      this.hintRunning  = false;
      this.hintProgress = 100;
    },
  },
};
</script>

<style scoped>
/* ── Page layout ─────────────────────────────────────────── */
.fw-page {
  max-width: 400px;
  margin: 0 auto;
  padding: 0.75rem;
  font-family: var(--font-body);
}

/* ── Header ──────────────────────────────────────────────── */
.fw-header {
  text-align: center;
  margin-bottom: 1.25rem;
}

.fw-title {
  font-family: var(--font-brand);
  font-size: 2rem;
  color: var(--color-primary-dark);
  margin: 0 0 0.25rem;
}

.fw-subtitle {
  color: var(--color-text-muted);
  font-size: 0.9rem;
  margin: 0;
}

/* ── Loading ─────────────────────────────────────────────── */
.fw-loading {
  text-align: center;
  padding: 3rem 0;
  color: var(--color-text-muted);
}

.fw-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--color-border);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── Grid wrapper ────────────────────────────────────────── */
.fw-grid-wrap {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  padding: 0.45rem;
  margin-bottom: 1rem;
  user-select: none;
  touch-action: none;
}

.fw-grid {
  display: grid;
  gap: 4px;
}

/* ── Cells ───────────────────────────────────────────────── */
.fw-cell {
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.15s ease, background-color 0.3s ease;
  /* Default: all cells look the same neutral grey */
  background: #dde1e7;
  color: var(--color-text);
}

/* Empty cells (no word) - slightly different shade, not interactive */
.fw-cell--empty {
  background: #c8cdd5;
  cursor: default;
}

/* Word cells not yet found - same neutral as base, no hint */
.fw-cell--unplayed {
  background: #dde1e7;
}

/* Reveal word colours only after the word is found */
.fw-word-0.fw-cell--found { background-color: #f97316; color: #fff; }
.fw-word-1.fw-cell--found { background-color: #22c55e; color: #fff; }
.fw-word-2.fw-cell--found { background-color: #06b6d4; color: #fff; }
.fw-word-3.fw-cell--found { background-color: #8b5cf6; color: #fff; }
.fw-word-4.fw-cell--found { background-color: #ec4899; color: #fff; }
.fw-word-5.fw-cell--found { background-color: #eab308; color: #1e293b; }

/* Found cells: slight inner glow */
.fw-cell--found {
  box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.5);
}

/* Active selection: teal highlight while dragging - colour matches the word */
.fw-cell--selected { transform: scale(1.08); z-index: 1; color: #fff !important; }

/* Per-word selection colours (match found colours) */
.fw-cell--selected.fw-sel-0 { background: #f97316 !important; box-shadow: 0 0 0 3px #fff, 0 0 0 5px rgba(249,115,22,0.45); }
.fw-cell--selected.fw-sel-1 { background: #22c55e !important; box-shadow: 0 0 0 3px #fff, 0 0 0 5px rgba(34,197,94,0.45); }
.fw-cell--selected.fw-sel-2 { background: #06b6d4 !important; box-shadow: 0 0 0 3px #fff, 0 0 0 5px rgba(6,182,212,0.45); }
.fw-cell--selected.fw-sel-3 { background: #8b5cf6 !important; box-shadow: 0 0 0 3px #fff, 0 0 0 5px rgba(139,92,246,0.45); }
.fw-cell--selected.fw-sel-4 { background: #ec4899 !important; box-shadow: 0 0 0 3px #fff, 0 0 0 5px rgba(236,72,153,0.45); }
.fw-cell--selected.fw-sel-5 { background: #eab308 !important; color: #1e293b !important; box-shadow: 0 0 0 3px #fff, 0 0 0 5px rgba(234,179,8,0.45); }

/* Start cell of a found word - subtle inner ring */
.fw-cell--start::after {
  content: "";
  position: absolute;
  inset: 3px;
  border-radius: 7px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  pointer-events: none;
}

.fw-cell__letter {
  font-family: var(--font-brand);
  font-size: clamp(0.7rem, 3vw, 1rem);
  font-weight: 700;
  line-height: 1;
  pointer-events: none;
}

.fw-cell__check {
  position: absolute;
  bottom: 3px;
  right: 4px;
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.9);
}

/* ── Top bar ─────────────────────────────────────────────── */
.fw-top-bar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.75rem;
}

.fw-hint-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.35rem;
  min-width: 120px;
}

.fw-hint-bar-track {
  width: 100%;
  height: 6px;
  background: #fde68a;
  border-radius: 999px;
  overflow: hidden;
}

.fw-hint-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
  border-radius: 999px;
  transition: width 0.06s linear;
}

/* ── Word list ───────────────────────────────────────────── */
.fw-word-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
}

.fw-word-tag {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border-radius: 8px;
  padding: 4px 8px;
  width: fit-content;
  /* Neutral background until found */
  background: rgba(0, 0, 0, 0.05);
  transition: background 0.3s ease, opacity 0.3s ease;
}

.fw-word-tag--done {
  opacity: 0.6;
}

.fw-word-tag__letter {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  font-family: var(--font-brand);
  font-weight: 700;
  font-size: 0.85rem;
  background: #c8cdd5;
  color: var(--color-text);
}

/* Hidden tiles - same neutral, no text rendered */
.fw-word-tag__letter--hidden {
  background: #c8cdd5;
}

/* Traced tiles - lit up in the word's colour while user is dragging */
.fw-word-tag__letter--traced { color: #fff; }
.fw-word-tag__letter--traced.fw-word-tag__letter--traced-0 { background: #f97316; }
.fw-word-tag__letter--traced.fw-word-tag__letter--traced-1 { background: #22c55e; }
.fw-word-tag__letter--traced.fw-word-tag__letter--traced-2 { background: #06b6d4; }
.fw-word-tag__letter--traced.fw-word-tag__letter--traced-3 { background: #8b5cf6; }
.fw-word-tag__letter--traced.fw-word-tag__letter--traced-4 { background: #ec4899; }
.fw-word-tag__letter--traced.fw-word-tag__letter--traced-5 { background: #eab308; color: #1e293b; }

.fw-word-tag__check {
  margin-left: 6px;
  font-size: 1.1rem;
  color: #16a34a;
}

/* Word tag colour - ONLY once found */
.fw-word-tag--done.fw-word-tag--col-0 { background: rgba(249,115,22,0.12); }
.fw-word-tag--done.fw-word-tag--col-0 .fw-word-tag__letter { background: #f97316; color: #fff; }

.fw-word-tag--done.fw-word-tag--col-1 { background: rgba(34,197,94,0.12); }
.fw-word-tag--done.fw-word-tag--col-1 .fw-word-tag__letter { background: #22c55e; color: #fff; }

.fw-word-tag--done.fw-word-tag--col-2 { background: rgba(6,182,212,0.12); }
.fw-word-tag--done.fw-word-tag--col-2 .fw-word-tag__letter { background: #06b6d4; color: #fff; }

.fw-word-tag--done.fw-word-tag--col-3 { background: rgba(139,92,246,0.12); }
.fw-word-tag--done.fw-word-tag--col-3 .fw-word-tag__letter { background: #8b5cf6; color: #fff; }

.fw-word-tag--done.fw-word-tag--col-4 { background: rgba(236,72,153,0.12); }
.fw-word-tag--done.fw-word-tag--col-4 .fw-word-tag__letter { background: #ec4899; color: #fff; }

.fw-word-tag--done.fw-word-tag--col-5 { background: rgba(234,179,8,0.12); }
.fw-word-tag--done.fw-word-tag--col-5 .fw-word-tag__letter { background: #eab308; color: #1e293b; }

/* Active row while dragging */
.fw-word-tag--active {
  background: rgba(0,0,0,0.04);
}

/* ── Controls ────────────────────────────────────────────── */
.fw-controls {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-bottom: 1rem;
}

.fw-btn {
  padding: 0.55rem 1.4rem;
  border: none;
  border-radius: 50px;
  font-family: var(--font-brand);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.fw-btn:active { transform: scale(0.96); }

.fw-btn--primary {
  background: var(--color-primary);
  color: #fff;
  box-shadow: var(--shadow-sm);
}

.fw-btn--primary:hover { background: var(--color-primary-dark); }

.fw-btn--secondary {
  background: var(--color-surface);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-sm);
}

.fw-btn--secondary:hover { background: var(--color-primary-light); }

.fw-btn--hint {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
}

.fw-btn--hint:hover:not(:disabled) { background: #fde68a; }
.fw-btn--hint:disabled { opacity: 0.45; cursor: not-allowed; }

/* ── Wrong message ───────────────────────────────────────── */
.fw-wrong-msg {
  text-align: center;
  background: #fee2e2;
  color: #b91c1c;
  border-radius: var(--radius-sm);
  padding: 0.5rem 1rem;
  font-weight: 600;
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

.fw-fade-enter-active,
.fw-fade-leave-active { transition: opacity 0.3s ease; }
.fw-fade-enter, .fw-fade-leave-to { opacity: 0; }

/* ── Victory screen ──────────────────────────────────────── */
.fw-victory {
  text-align: center;
  padding: 2.5rem 1rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
}

.fw-victory__icon { font-size: 3.5rem; margin-bottom: 0.5rem; }

.fw-victory__title {
  font-family: var(--font-brand);
  font-size: 1.8rem;
  color: var(--color-primary-dark);
  margin: 0 0 0.4rem;
}

.fw-victory__sub {
  color: var(--color-text-muted);
  margin: 0 0 1.5rem;
}
</style>
